import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Search, ShoppingCart, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { formatProductPrice, getProductImage, getProductPath, getProducts } from '../services/productCatalog';
import PageHero from '../components/PageHero';

export default function ProductListing() {
  const { lang, t } = useLanguage();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addedProductId, setAddedProductId] = useState(null);

  useEffect(() => {
    let active = true;
    getProducts()
      .then((data) => {
        if (active) setProducts(data);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const categories = useMemo(
    () => [...new Set(products.map((product) => product.category).filter(Boolean))],
    [products],
  );

  const filteredProducts = products.filter((product) => {
    const query = searchQuery.trim().toLowerCase();
    const matchesCategory = activeCategory === 'all' || product.category === activeCategory;
    const matchesSearch = !query || [product.name, product.sku, product.strength_class, product.description]
      .some((value) => value?.toLowerCase().includes(query));
    return matchesCategory && matchesSearch;
  });

  const handleAddToCart = (product) => {
    const minimumVolume = Number(product.minimum_order_standard) || 5;
    addToCart(product, minimumVolume, 'standard');
    setAddedProductId(product.id);
  };

  return (
    <div className="master-product-page">
      <PageHero eyebrow="Katalog Master Produk" title="Produk & Layanan" subtitle="Seluruh produk aktif di halaman ini tersambung langsung dengan data master, harga, keranjang, dan proses checkout." image="/reference-images/ready-mix.webp" />

      <div className="breadcrumb-wrapper">
        <div className="container">
          <ul className="breadcrumb-list">
            <li><Link to="/">{t('nav_home')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><span className="current">{t('nav_products')}</span></li>
          </ul>
        </div>
      </div>

      <section className="catalog-content">
        <div className="container">
          <div className="catalog-toolbar">
            <div className="category-tabs" aria-label="Filter kategori produk">
              <button className={activeCategory === 'all' ? 'active' : ''} onClick={() => setActiveCategory('all')}>
                Semua Produk
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  className={activeCategory === category ? 'active' : ''}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
            <label className="catalog-search">
              <Search size={18} />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Cari nama, SKU, atau mutu beton..."
              />
            </label>
          </div>

          {loading && <div className="catalog-state">Memuat data master produk...</div>}
          {!loading && error && (
            <div className="catalog-state error">
              <strong>Produk belum dapat ditampilkan.</strong>
              <span>{error} Pastikan backend lokal berjalan di port 18081.</span>
            </div>
          )}
          {!loading && !error && filteredProducts.length === 0 && (
            <div className="catalog-state">Tidak ada produk yang sesuai dengan pencarian.</div>
          )}

          {!loading && !error && filteredProducts.length > 0 && (
            <div className="master-product-grid">
              {filteredProducts.map((product) => {
                const added = addedProductId === product.id;
                return (
                  <article className="master-product-card" key={product.id}>
                    <Link to={getProductPath(product)} className="master-product-image">
                      <img src={getProductImage(product)} alt={product.name} />
                      {product.strength_class && product.strength_class !== 'N/A' && (
                        <span>{product.strength_class}</span>
                      )}
                    </Link>
                    <div className="master-product-body">
                      <div className="master-product-meta">{product.category} · {product.sku}</div>
                      <h2><Link to={getProductPath(product)}>{product.name}</Link></h2>
                      <p>{product.description || product.use_case || 'Informasi produk tersedia melalui tim sales kami.'}</p>
                      <div className="master-product-specs">
                        <span><Truck size={14} /> Min. {Number(product.minimum_order_standard) || 5} m³</span>
                        {product.is_minimix_available ? <span>MiniMix tersedia</span> : <span>Truck standar</span>}
                      </div>
                      <div className="master-product-footer">
                        <div>
                          <small>Mulai dari</small>
                          <strong>{formatProductPrice(product.price_per_m3, lang === 'id' ? 'id-ID' : 'en-US')}<em>/m³</em></strong>
                        </div>
                        <div className="product-card-actions">
                          <Link to={getProductPath(product)} className="detail-product-button" aria-label={`Detail ${product.name}`}>
                            <ChevronRight size={18} />
                          </Link>
                          {added ? (
                            <Link to="/cart" className="add-cart-button added">Lihat Keranjang</Link>
                          ) : (
                            <button className="add-cart-button" onClick={() => handleAddToCart(product)}>
                              <ShoppingCart size={16} /> Tambah
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <style>{`
        .catalog-content { padding: 70px 0 100px; }
        .catalog-toolbar { display: flex; align-items: flex-start; justify-content: space-between; gap: 28px; margin-bottom: 42px; }
        .category-tabs { display: flex; flex-wrap: wrap; gap: 9px; }
        .category-tabs button { min-height: 42px; padding: 8px 16px; border: 1px solid #deded8; color: #444; font-size: 12px; font-weight: 700; }
        .category-tabs button:hover,
        .category-tabs button.active { border-color: #777654; background: #777654; color: #fff; }
        .catalog-search { flex: 0 0 330px; height: 44px; display: flex; align-items: center; gap: 10px; padding: 0 14px; border: 1px solid #deded8; }
        .catalog-search input { width: 100%; border: 0; outline: 0; color: #333; font: inherit; font-size: 13px; }
        .catalog-state { padding: 70px 24px; display: flex; flex-direction: column; gap: 8px; text-align: center; background: #f5f5f1; color: #666; }
        .catalog-state.error strong { color: #b42318; }
        .master-product-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 30px; }
        .master-product-card { min-width: 0; border: 1px solid #deded8; background: #fff; transition: transform .25s ease, box-shadow .25s ease; }
        .master-product-card:hover { transform: translateY(-4px); box-shadow: 0 12px 28px rgba(0,0,0,.1); }
        .master-product-image { position: relative; display: block; height: 250px; overflow: hidden; background: #eee; }
        .master-product-image img { width: 100%; height: 100%; object-fit: cover; transition: transform .35s ease; }
        .master-product-card:hover .master-product-image img { transform: scale(1.035); }
        .master-product-image > span { position: absolute; top: 16px; left: 16px; padding: 7px 10px; background: #bbd448; color: #252525; font-size: 11px; font-weight: 800; }
        .master-product-body { padding: 24px; }
        .master-product-meta { margin-bottom: 8px; color: #777654; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .5px; }
        .master-product-body h2 { min-height: 52px; margin-bottom: 10px; font-size: 19px; line-height: 1.35; }
        .master-product-body h2 a:hover { color: #777654; }
        .master-product-body > p { min-height: 66px; margin-bottom: 16px; color: #666; font-size: 13px; line-height: 1.65; }
        .master-product-specs { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
        .master-product-specs span { display: inline-flex; align-items: center; gap: 5px; padding: 5px 8px; background: #f5f5f1; color: #555; font-size: 10px; font-weight: 700; }
        .master-product-footer { padding-top: 18px; border-top: 1px solid #ecece8; display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; }
        .master-product-footer small { display: block; color: #777; font-size: 10px; }
        .master-product-footer strong { display: block; color: #252525; font-size: 17px; }
        .master-product-footer em { margin-left: 2px; color: #777; font-size: 10px; font-style: normal; font-weight: 500; }
        .product-card-actions { display: flex; align-items: center; gap: 7px; }
        .detail-product-button { width: 38px; height: 38px; display: grid; place-items: center; border: 1px solid #777654; color: #777654; }
        .add-cart-button { min-height: 38px; padding: 0 12px; display: inline-flex; align-items: center; justify-content: center; gap: 6px; background: #777654; color: #fff; font-size: 11px; font-weight: 800; }
        .add-cart-button:hover,
        .add-cart-button.added { background: #65644a; color: #fff; }
        @media (max-width: 1024px) {
          .catalog-toolbar { flex-direction: column; }
          .catalog-search { width: 100%; flex-basis: 44px; }
          .master-product-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 640px) {
          .catalog-content { padding: 48px 0 70px; }
          .master-product-grid { grid-template-columns: 1fr; }
          .master-product-image { height: 230px; }
        }
      `}</style>
    </div>
  );
}
